import { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../../config';

const useUnreadEmailsCount = () => {
    const [count, setCount] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let isMounted = true;
        let isAuthorized = true;

        const fetchUnreadCount = async () => {
            if (!isMounted || !isAuthorized) return;

            try {
                const authUser = sessionStorage.getItem('authUser');
                let token = null;
                
                if (authUser) {
                    try {
                        const parsed = JSON.parse(authUser);
                        token = parsed.token || parsed.data?.token;
                    } catch (error) {
                        console.error('Error parsing authUser:', error);
                    }
                }

                if (!token) {
                    return;
                }

                const response = await axios.get(
                    `${config.api.API_URL}/emails/unread/count`,
                    {
                        headers: { 'Authorization': `Bearer ${token}` }
                    }
                );

                if (isMounted && response.data.success) {
                    setCount(response.data.data.count || 0);
                    setError(null);
                }
            } catch (error: any) {
                if (error?.response?.status === 401) {
                    isAuthorized = false;
                }
                if (isMounted) {
                    console.error('Error fetching unread emails count:', error);
                    setError(error.message);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchUnreadCount();

        // Poll every 30 seconds
        const interval = setInterval(() => {
            if (isAuthorized) {
                fetchUnreadCount();
            }
        }, 30000);

        // Listen for custom events that might change email status
        const handleEmailUpdate = () => {
            fetchUnreadCount();
        };

        window.addEventListener('email-processed', handleEmailUpdate);
        window.addEventListener('email-imported', handleEmailUpdate);
        window.addEventListener('emails-synced', handleEmailUpdate);

        return () => {
            isMounted = false;
            clearInterval(interval);
            window.removeEventListener('email-processed', handleEmailUpdate);
            window.removeEventListener('email-imported', handleEmailUpdate);
            window.removeEventListener('emails-synced', handleEmailUpdate);
        };
    }, []);

    const refresh = async () => {
        setLoading(true);
        const authUser = sessionStorage.getItem('authUser');
        let token = null;
        
        if (authUser) {
            try {
                const parsed = JSON.parse(authUser);
                token = parsed.token || parsed.data?.token;
            } catch (error) {
                console.error('Error parsing authUser:', error);
            }
        }

        if (!token) {
            setLoading(false);
            return;
        }

        try {
            const response = await axios.get(
                `${config.api.API_URL}/emails/unread/count`,
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );

            if (response.data.success) {
                setCount(response.data.data.count || 0);
                setError(null);
            }
        } catch (error: any) {
            console.error('Error refreshing unread emails count:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    return { count, loading, error, refresh };
};

export default useUnreadEmailsCount;